import { ContentTypeName, SUPPORTED_LOCALES } from '@/constants';
import {
  ContentItemUITemplateKeys,
  type ICarrierInformation,
  type ILandingPage,
  type ILeaderboardEntry,
  type ITrackingPageInsights,
  type ITrackingStat,
  type SectionItem,
} from '@/interfaces';

// Contentful arrays can carry null items and the JSON Field Editor can leave half-filled rows
// behind — drop both, and treat an empty list as "no section".
const presentItems = <T>(items: Array<T | null | undefined> | undefined, isComplete: (item: T) => boolean) => {
  const kept = (items ?? []).filter((item): item is T => !!item && isComplete(item));
  return kept.length ? kept : undefined;
};

/**
 * The carrier website as a safe absolute link, or undefined.
 *
 * The field is free text from the Growth sheet: a bare `www.ups.com` would otherwise become a
 * relative link (`/en/www.ups.com`), and nothing else stops a non-web scheme reaching an `href`.
 */
export const normalizeWebsiteUrl = (url?: string): string | undefined => {
  const trimmed = url?.trim();
  if (!trimmed) return undefined;

  const hasScheme = /^[a-z][a-z\d+.-]*:\/\//i.test(trimmed) || /^(javascript|data|mailto|tel|vbscript):/i.test(trimmed);

  try {
    const parsed = new URL(hasScheme ? trimmed : `https://${trimmed.replace(/^\/+/, '')}`);
    return parsed.protocol === 'https:' || parsed.protocol === 'http:' ? parsed.href : undefined;
  } catch {
    return undefined;
  }
};

const normalizeCarrierInformation = (information?: ICarrierInformation): ICarrierInformation | undefined => {
  if (!information?.name) return undefined;

  return { ...information, website: normalizeWebsiteUrl(information.website) };
};

/**
 * Normalises the six tracking-insight JSON fields of a landing page into what the sections render.
 *
 * Returns undefined when the page carries none of them — every non-SEO landing page, and any
 * SEO page the Growth sheet has not covered yet — so the caller can treat "no insights" and
 * "no data for this block" the same way: the section is simply not on the page.
 */
export const getTrackingPageInsights = (data?: ILandingPage | null): ITrackingPageInsights | undefined => {
  if (!data) return undefined;

  const trackingStats = presentItems(data.trackingStats?.items, (stat) => !!stat.value && !!stat.label);

  const insights: ITrackingPageInsights = {
    trackingStats,
    // Only meaningful alongside the figures it heads, so it never makes a page count as covered.
    trackingStatsTitle: trackingStats ? data.trackingStats?.title?.trim() || undefined : undefined,
    topCarriers: presentItems(data.topCarriers?.items, (entry) => !!entry.name),
    topMerchants: presentItems(data.topMerchants?.items, (entry) => !!entry.name),
    reviews: presentItems(data.customerReviews?.items, (review) => !!review.quote),
    carrierInformation: normalizeCarrierInformation(data.carrierInformation),
    howTrackingWorks: data.howTrackingWorks?.content?.content?.length ? data.howTrackingWorks : undefined,
  };

  return Object.values(insights).some(Boolean) ? insights : undefined;
};

const isMetricsWrapper = (section: SectionItem): boolean =>
  section.__typename?.toLowerCase() === ContentTypeName.Wrapper.toLowerCase() &&
  Array.isArray(section.entryList) &&
  section.entryList.some((item) => item?.uiTemplate === ContentItemUITemplateKeys.Metrics);

/**
 * Swaps this page's own figures into the shared "Join 1 million+ users" band.
 *
 * Every SEO page links the same `[SEO Pages] Metrics Entry Template` wrapper, whose five
 * content items hold translation keys. When the page has `trackingStats`, those items are
 * replaced in place — same wrapper, same position, same styling — so a country or carrier page
 * shows its two figures and a page without the field keeps the site-wide five. The `Metrics`
 * template passes title and subtitle through `t()`, which returns a string it has no entry for
 * unchanged, so a literal "1,450+" prints as-is.
 */
export const applyTrackingStats = (
  sectionList: SectionItem[],
  stats: ITrackingStat[] | undefined,
  title?: string,
): SectionItem[] => {
  if (!stats?.length) return sectionList;

  return sectionList.map((section) => {
    if (!isMetricsWrapper(section)) return section;

    return {
      ...section,
      title: title || section.title,
      entryList: stats.map((stat) => ({
        __typename: 'ContentItem',
        uiTemplate: ContentItemUITemplateKeys.Metrics,
        title: stat.value,
        subTitle: stat.label,
      })),
    };
  });
};

/**
 * Localized name of the place a country or region page is about, for the leaderboard headings
 * ("Top 10 Carriers in Deutschland").
 *
 * `placeCode` is the page's ISO 3166-1 alpha-2 country code, or a UN M.49 area code for a region
 * (142 = Asia, 150 = Europe, …), both of which `Intl.DisplayNames` resolves in every locale the
 * site ships. Undefined when the page has no code or the runtime cannot resolve it — the caller
 * then falls back to the heading without a place rather than printing a raw code.
 */
export const getPlaceName = (placeCode: string | undefined, locale: string): string | undefined => {
  const code = placeCode?.trim();
  if (!code || typeof Intl === 'undefined' || typeof Intl.DisplayNames !== 'function') return undefined;

  try {
    const name = new Intl.DisplayNames([locale], { type: 'region', fallback: 'none' }).of(code);
    return name && name !== code ? name : undefined;
  } catch {
    return undefined;
  }
};

const FALLBACK_LOCALE = 'en';

/**
 * BCP-47 locale for `Intl` formatting on a tracking page.
 *
 * SEO pages carry their language in the first path segment (`/vi/track-global`), not in a Next.js
 * i18n config. The value is checked against SUPPORTED_LOCALES because `Intl` throws a RangeError on
 * an unrecognised tag — an arbitrary first segment (a 404 path, a crawler probing) would otherwise
 * take the page down rather than just mis-format.
 */
const resolveInsightsLocale = (articleTypeSlug?: string): string =>
  articleTypeSlug && SUPPORTED_LOCALES.includes(articleTypeSlug) ? articleTypeSlug : FALLBACK_LOCALE;

/**
 * The page's place name, resolved once on the server.
 *
 * Server-only: browser ICU data does not carry the UN M.49 area names (Chrome returns undefined
 * for `142` Asia), so resolving it during render would cause a hydration mismatch. `null` rather
 * than undefined because it travels through getServerSideProps.
 */
export const getSeoPagePlaceName = (entry: ILandingPage | null | undefined, articleTypeSlug?: string): string | null =>
  getPlaceName(entry?.placeCode, resolveInsightsLocale(articleTypeSlug)) ?? null;

/**
 * Brands for the partner strip under the hero: the page's top carriers followed by its top
 * merchants, each brand once. A brand that ranks in both lists (Amazon ships as a carrier and
 * sells as a merchant) keeps its first appearance.
 */
export const getPartnerLogos = (insights?: ITrackingPageInsights): ILeaderboardEntry[] => {
  const seen = new Set<string>();
  const logos: ILeaderboardEntry[] = [];

  for (const entry of [...(insights?.topCarriers ?? []), ...(insights?.topMerchants ?? [])]) {
    const key = entry.name.trim().toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    logos.push(entry);
  }

  return logos;
};
